import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import ChatBotIcon from '../../../features/chatbot/components/ChatBotIcon';
import { useAuthContext } from '../../../features/auth/data/context/AuthContext';
import Alert from '../Alert';
import Loader from '../Loader';
import './styles/LayoutStyles.css';

const DashboardLayout: React.FC = () => {
  const { state, fetchProfile, logout } = useAuthContext();
  const token = state.token || localStorage.getItem('iqadmin_token');
  const hasFetchedRef = useRef(false);

  const [showInactivityPopup, setShowInactivityPopup] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);

  const userLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout]);

  const resetIdleTimer = useCallback((freezePopUp: boolean) => {
    if (freezePopUp) {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return;
    }

    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    // Show warning popup after 14 minutes
    warningTimerRef.current = setTimeout(() => {
      setShowInactivityPopup(true);
    }, 14 * 60 * 1000);

    // Auto logout after 15 minutes
    idleTimerRef.current = setTimeout(() => {
      setShowInactivityPopup(false);
      userLogout();
      resetIdleTimer(true);
    }, 15 * 60 * 1000);
  }, [userLogout]);

  useEffect(() => {
    if (!token) return;

    resetIdleTimer(false);

    const resetActivityTimer = () => {
      resetIdleTimer(false);
    };

    window.addEventListener("mousemove", resetActivityTimer);
    window.addEventListener("keydown", resetActivityTimer);

    return () => {
      window.removeEventListener("mousemove", resetActivityTimer);
      window.removeEventListener("keydown", resetActivityTimer);
      resetIdleTimer(true);
    };
  }, [resetIdleTimer, token]);


  useEffect(() => {
    if (token && !state.user && !state.isProfileLoading && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchProfile();
    }
  }, [token, state.user, state.isProfileLoading, fetchProfile]);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  const profile = state.user;
  const userName = (profile as any)?.admin_username || (profile as any)?.full_name || profile?.company_name || 'Institute';
  const userEmail = profile?.email_address || state.email || '';

  return (
    <div className="dashboard-layout">
      <Topbar userName={userName} userEmail={userEmail} />
      <div className="bottom-container">
        <Sidebar />
        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>

      <Loader isLoading={isLoggingOut} message="Logging out..." />

      <ChatBotIcon />

      <Alert
        show={showInactivityPopup}
        title="Session Timeout Warning"
        message="We have not detected any user activity for a while. Do you want to logout?"
        type="warning"
        primaryButtonText="Logout"
        onPrimaryClick={() => {
          userLogout();
          resetIdleTimer(true);
        }}
        secondaryButtonText="Stay Logged In"
        onSecondaryClick={() => {
          resetIdleTimer(false);
          setShowInactivityPopup(false);
        }}
        onClose={() => {
          resetIdleTimer(false);
          setShowInactivityPopup(false);
        }}
      />
    </div>
  );
};

export default DashboardLayout;
